import { Box, Flex, Loading, Typography } from '@iheartjane/reefer';
import type { AerosyncWidget } from 'aerosync-web-sdk';
import { useCallback, useEffect, useState } from 'react';
import type { MutableRefObject } from 'react';

import { useMfa } from '../../../../providers/mfa/mfaProvider';
import { Mfa } from '../../../mfa/mfa';

/**
 * Renders the Aerosync widget gated behind MFA verification.
 * Requires an `MfaProvider` ancestor — uses `useMfa()` internally
 * for code-send/verify state.
 */
export interface AeropayScreenProps {
  /** Function called when the user successfully verifies their MFA */
  afterVerifySuccess?: () => void;

  /** Contact display string: masked phone "( ••• ) ••• - 1234" or email */
  contactDisplay: string;

  /** Is the user linking a bank account */
  isLinkingBankAccount: boolean;

  /** Is the widget loading */
  isLoadingWidget: boolean;

  /** Function called when the user cancels the MFA */
  onCancel?: () => void;

  /**
   * Side-effect run on every successful verification (whether MFA was shown
   * or auto-skipped because the user verified recently). Used to fire a deferred user
   * update once MFA has been satisfied.
   */
  onVerified?: () => void;

  /** Whether the user requires MFA */
  requiresMfa?: boolean;

  /** Whether to show the cancel button */
  showCancelButton?: boolean;

  /** Whether the contact method is email vs phone */
  usesEmail?: boolean;

  /** Reference to the Aerosync widget */
  widgetRef: MutableRefObject<AerosyncWidget | null>;
}

export const WIDGET_CONTAINER_ID = 'aerosync-widget';

export const AeropayScreen = ({
  afterVerifySuccess,
  contactDisplay,
  isLinkingBankAccount,
  isLoadingWidget,
  onCancel,
  onVerified,
  requiresMfa = true,
  showCancelButton = true,
  usesEmail = false,
  widgetRef,
}: AeropayScreenProps) => {
  const [userVerified, setUserVerified] = useState(false);

  const {
    canResendCode,
    canSendCode,
    codeResent,
    codeSent,
    errorSendingCode,
    errorVerifyingCode,
    resendCode,
    sendCode,
    sendingCode,
    verifyCode,
  } = useMfa();

  const handleVerifySuccess = useCallback(() => {
    setUserVerified(true);
    onVerified?.();
    if (afterVerifySuccess) {
      afterVerifySuccess();
    } else {
      widgetRef.current && widgetRef.current.launch();
    }
  }, [afterVerifySuccess, onVerified, widgetRef]);

  const handleVerifyCode = useCallback(
    async (code: string) => {
      const success = await verifyCode(code);
      if (success) handleVerifySuccess();
    },
    [verifyCode, handleVerifySuccess]
  );

  const isLoading = isLinkingBankAccount || isLoadingWidget;

  useEffect(() => {
    if (!userVerified && !isLoadingWidget && !requiresMfa) {
      handleVerifySuccess();
    }
  }, [userVerified, isLoadingWidget, requiresMfa, handleVerifySuccess]);

  return (
    <>
      {isLinkingBankAccount && (
        <Flex width="100%" flexDirection="column" alignItems="center">
          <Box position="relative" mb={24}>
            <Loading color="black" size="lg" />
          </Box>
          <Typography branded variant="h2-semibold" textAlign="center" mb={8}>
            Hang tight
          </Typography>
          <Typography textAlign="center">
            This may take a few seconds.
          </Typography>
        </Flex>
      )}

      <Box
        width={userVerified && !isLoading ? '342px' : 'auto'}
        height={userVerified && !isLoading ? '633px' : 'auto'}
      >
        {!userVerified && requiresMfa && !isLinkingBankAccount && (
          <Mfa
            autoSendCode={canSendCode()}
            codeResent={codeResent}
            codeSent={codeSent}
            contactDisplay={contactDisplay}
            errorSendingCode={errorSendingCode}
            errorVerifyingCode={errorVerifyingCode}
            isLoading={isLoadingWidget}
            onCancel={onCancel}
            resendCode={resendCode}
            sendCode={sendCode}
            sendingCode={sendingCode}
            showCancelButton={showCancelButton}
            showResendButton={canResendCode()}
            usesEmail={usesEmail}
            verifyCode={handleVerifyCode}
          />
        )}

        <div id={WIDGET_CONTAINER_ID}></div>
      </Box>
    </>
  );
};
